import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { Loader2Icon } from 'lucide-react'

import { IRQCategorySeverity, OrgCompanyEngagement } from '@/gen/inventory/v1/company_service_pb'
import { RiskLevel } from '@/gen/inventory/v1/risk_pb'

import { RISK_LEVELS } from '@/lib/connect/proto-types'
import { cn } from '@/lib/ui/style-helpers'

import { IrqCategoryList } from '@/pages/company-drawer/company-overview/set-irq-modal/irq-category-list'
import { SET_IRQ_MODAL_LABEL } from '@/pages/company-drawer/company-overview/set-irq-modal/labels'

import { RadioGroupItem } from '@/components/lemmala-design-system/radio-group/radio-group'
import { Textarea } from '@/components/lemmala-design-system/textarea/textarea'
import { RiskBadge } from '@/components/shared/risk-badge/risk-badge'
import { toDisplayRisk } from '@/components/shared/risk-badge/utils'

type SelectInherentRiskStepProps = {
  suggestedRiskLevel: RiskLevel
  comment: string
  setComment: (comment: string) => void
  setSelectedRiskLevel: (riskLevel: RiskLevel) => void
  selectedRiskLevel: RiskLevel
  irqCategorySeverities?: IRQCategorySeverity[]
  isEstimated: boolean
  isLoadingRisk: boolean
  targetName: string
  engagements?: OrgCompanyEngagement[]
  currentRiskLevel: RiskLevel
}

export const SelectInherentRiskStep = ({
  suggestedRiskLevel,
  comment,
  setComment,
  setSelectedRiskLevel,
  selectedRiskLevel,
  irqCategorySeverities = [],
  isEstimated,
  isLoadingRisk,
  targetName,
  engagements,
  currentRiskLevel,
}: SelectInherentRiskStepProps) => {
  const effective =
    selectedRiskLevel === RiskLevel.UNSPECIFIED ? suggestedRiskLevel : selectedRiskLevel

  const showEngagements = engagements !== undefined

  return (
    <div className='flex min-h-full flex-col rounded-md border border-main-border-default px-6'>
      <h3 className='py-4 text-main-foreground-primary text-heading-xxsmall'>
        {SET_IRQ_MODAL_LABEL.ASSIGN_RISK_HEADING(targetName)}
      </h3>

      {isLoadingRisk ? (
        <div className='flex flex-1 items-center justify-center pb-6'>
          <Loader2Icon className='size-5 animate-spin text-main-foreground-tertiary' />
        </div>
      ) : (
        <div className='flex flex-1 flex-col gap-6 pb-6'>
          <div className='flex gap-6'>
            {showEngagements ? (
              <div className='flex w-48 shrink-0 flex-col gap-4 pt-2'>
                <span className='text-main-foreground-primary text-label-default'>
                  {SET_IRQ_MODAL_LABEL.ENGAGEMENTS}
                </span>
                <ul className='flex flex-col gap-4'>
                  {engagements.map((engagement) => {
                    const { riskLevel, isEstimated } = toDisplayRisk(
                      engagement.validatedRiskLevel,
                      engagement.calculatedRiskLevel,
                    )
                    return (
                      <li key={engagement.id} className='flex items-center gap-1.5'>
                        {riskLevel !== RiskLevel.UNSPECIFIED && (
                          <RiskBadge
                            riskLevel={riskLevel}
                            size='icon'
                            variant={isEstimated ? 'estimated' : 'default'}
                          />
                        )}
                        <span className='text-main-foreground-primary text-body-default'>
                          {engagement.name}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ) : (
              irqCategorySeverities.length > 0 && (
                <IrqCategoryList
                  irqCategorySeverities={irqCategorySeverities}
                  isEstimated={isEstimated}
                />
              )
            )}

            <RadioGroupPrimitive.Root
              value={String(effective)}
              onValueChange={(v) => setSelectedRiskLevel(Number(v) as RiskLevel)}
              className='flex min-w-0 flex-1 flex-col gap-2'
            >
              {RISK_LEVELS.map(({ number: risk }) => {
                const isSelected = effective === risk
                const isProjected = risk === suggestedRiskLevel
                const isCurrent = risk === currentRiskLevel
                return (
                  <label
                    key={risk}
                    className={cn(
                      'flex cursor-pointer select-none items-center justify-between gap-3 rounded-md border p-3 transition-colors',
                      isSelected
                        ? 'border-main-border-accent ring-1 ring-main-border-accent'
                        : 'border-main-border-default hover:bg-main-background-interactive-list-hover',
                    )}
                  >
                    <div className='flex items-center gap-3'>
                      <RadioGroupItem value={String(risk)} />
                      <RiskBadge riskLevel={risk} variant='highlighted' />
                    </div>
                    {isProjected && !isCurrent && (
                      <span className='text-main-foreground-primary text-body-default'>
                        {SET_IRQ_MODAL_LABEL.CALCULATED_IR}
                      </span>
                    )}
                    {isCurrent && (
                      <span className='text-main-foreground-tertiary text-body-default'>
                        {SET_IRQ_MODAL_LABEL.CURRENT_IR}
                      </span>
                    )}
                  </label>
                )
              })}
            </RadioGroupPrimitive.Root>
          </div>

          <div className='flex flex-col gap-2'>
            <span className='text-main-foreground-primary text-label-default'>
              {SET_IRQ_MODAL_LABEL.COMMENT}{' '}
              <span className='text-main-foreground-tertiary'>
                {SET_IRQ_MODAL_LABEL.COMMENT_OPTIONAL}
              </span>
            </span>
            <Textarea
              placeholder={SET_IRQ_MODAL_LABEL.COMMENT_PLACEHOLDER}
              onChange={(e) => setComment(e.target.value)}
              value={comment}
            />
          </div>
        </div>
      )}
    </div>
  )
}
